import FilePreviewComponent from '@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue';
import {
    ACTIONS,
    ADMIN_VERIFY_APPLICATION_URL,
    get_headers,
    GET_TABS_DETAILS,
    get_user_role,
    QUESTION_TYPES,
    REQUEST_INFO_URL,
    SUBMIT_APPLICATION_FORMS_ANSWERS,
    VERIFY_SECTION
} from "@/utils/constants/constants";
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
    validate_non_empty_string,
    validate_non_sub_zero_number
} from '@/utils/validations/validate_inputs';
import axios from "axios";

export default {
    name: "RenderSections",
    mixins: [alert_mixin],
    components: { FilePreviewComponent },
    emits: ['files-saved'],
    props: {
        form_main_section_id: { type: [String, Number], default: null },
        application_id: { type: [String, Number], default: null },
        application_form_id: { type: [String, Number], default: null },
        entity_profile_id: { type: [String, Number], default: null },
        current_status_id: { type: [String, Number], default: null },
        isReadOnly: { type: Boolean, default: false },
    },

    data: () => ({
        isLoading: false,
        safeFormConfig: [],
        safeForms: [],
        open_map: {},
        currentFormIndex: 0,
        showFilePreviewModal: false,
        request_info_note: "",
        answered_file_questions: [],
        requested_field_ids: [],
        userRole: null,
        selected_request_fields: [],
        preview_modal: false,
        selected_url: null,
        allowed_actions: [],
        QUESTION_TYPES,
        ACTIONS
    }),

    computed: {
        progressPercentage() {
            const total = this.safeFormConfig.length || 1;
            const pct = Math.round(((this.currentFormIndex + 1) / total) * 100);
            return `${pct}%`;
        },
        is_verify_allowed() {
            if (this.is_system_admin_user) {
                return true;
            }
            return this.allowed_actions.some(action =>
                action.conditions_met === true &&
                action.action.includes(ACTIONS.VERIFY)
            );
        },

        is_request_information_allowed() {
            if (this.is_system_admin_user) {
                return true;
            }
            return this.allowed_actions.some(action =>
                action.conditions_met === true &&
                action.action.includes(ACTIONS.REQUEST_INFORMATION)
            );
        },

        can_submit() {
            if (this.isReadOnly) {
                return false;
            }
            const currentForm = this.get_safe_form_config(this.currentFormIndex);
            if (currentForm && this.hasAnyWriteAccess(currentForm)) {
                return true;
            }
            return this.is_submit_allowed;
        },

        is_entity_user() {
            return this.userRole && this.userRole.toLowerCase() === 'entity';
        },
        is_system_admin_user() {
            return this.userRole && this.userRole.toLowerCase() === 'system admin';
        },
        hasAnySectionAccess() {
            if (!Array.isArray(this.safeFormConfig) || this.safeFormConfig.length === 0) {
                return false;
            }
            return this.safeFormConfig.some(form =>
                Array.isArray(form.sections) && form.sections.some(section => this.hasSectionAccess(section))
            );
        }
    },

    watch: {
        form_main_section_id() { this.load_tabs_details(); },
        application_id() { this.load_tabs_details(); },
        application_form_id() { this.load_tabs_details(); },
        current_status_id() { this.load_tabs_details(); },
    },

    mounted() {
        this.load_tabs_details();
        this.headers = get_headers();
        this.userRole = get_user_role();
        const el = document.getElementById('request_information_modal');
        if (el) {
            el.addEventListener('show.bs.modal', () => {
                this.reset_request_information_state();
            });
            el.addEventListener('hidden.bs.modal', () => {
                this.reset_request_information_state();
            });
        }
    },

    methods: {
        normalize_is_verified(value) {
            if (typeof value === "boolean") {
                return value;
            }

            if (typeof value === "number") {
                return value === 1;
            }

            const normalized = String(value ?? "").trim().toLowerCase();
            return ["1", "true", "yes", "verified"].includes(normalized);
        },

        handleNumberInput(event, field, fIdx) {
            if (this.isReadOnly) {
                event.target.value = this.get_safe_form(fIdx)?.[field.model] ?? "";
                return;
            }
            const value = event.target.value.replace(/\D/g, '')
            Reflect.set(this.safeForms.at(fIdx), field.model, value)
        },

        hasSectionAccess(section) {
            if (this.is_entity_user) {
                return true;
            }
            if (this.is_system_admin_user) {
                return true;
            }
            return section.access &&
                section.access.action_name &&
                section.access.action_name !== ACTIONS.NO_ACCESS;
        },

        hasWriteAccess(section) {
            if (this.isReadOnly) {
                return false;
            }
            if (this.is_entity_user) {
                return true;
            }
            if (this.is_system_admin_user) {
                return true;
            }
            return section.access &&
                section.access.action_name === ACTIONS.WRITE;
        },

        hasReadAccess(section) {
            if (this.is_entity_user) {
                return false;
            }
            return section.access &&
                section.access.action_name === ACTIONS.READ;
        },

        hasAnyWriteAccess(form) {
            if (this.isReadOnly) {
                return false;
            }
            return form.sections && form.sections.some(section =>
                this.hasSectionAccess(section) && this.hasWriteAccess(section)
            );
        },

        isAdvertisedPostSection(section) {
            const normalizedTitle = String(section?.title || "")
                .trim()
                .toLowerCase()
                .replace(/\s+/g, " ");

            return normalizedTitle === "advertised post";
        },

        isAdvertisedPostReadOnlyQuestion(section, fieldIndex) {
            return this.isAdvertisedPostSection(section) &&
                Number.isInteger(fieldIndex) &&
                fieldIndex >= 0 &&
                fieldIndex < 3;
        },

        canEditSectionQuestion(section, fieldIndex) {
            if (!this.hasWriteAccess(section)) {
                return false;
            }

            return !this.isAdvertisedPostReadOnlyQuestion(section, fieldIndex);
        },

        isLastVisibleSection(form, sectionIndex) {
            if (!form?.sections?.length) {
                return false;
            }

            const visibleSectionIndices = form.sections
                .map((section, index) => (this.hasSectionAccess(section) ? index : null))
                .filter(index => index !== null);

            return visibleSectionIndices.length > 0 &&
                visibleSectionIndices[visibleSectionIndices.length - 1] === sectionIndex;
        },

        get_safe_form(field_index) {
            if (!Array.isArray(this.safeForms)) {
                return null;
            }
            if (Reflect.has(this.safeForms, field_index)) {
                return Reflect.get(this.safeForms, field_index);
            }
            return null;
        },

        setSafeForm(field_index, value) {
            if (!Array.isArray(this.safeForms)) {
                this.safeForms = [];
            }
            return Reflect.set(this.safeForms, field_index, value);
        },

        get_safe_form_config(field_index) {
            if (!Array.isArray(this.safeFormConfig)) {
                return null;
            }
            if (Reflect.has(this.safeFormConfig, field_index)) {
                return Reflect.get(this.safeFormConfig, field_index);
            }
            return null;
        },

        file_exitsts(field_index, field) {
            const safeForm = this.get_safe_form(field_index);
            if (!safeForm) return false;

            const v = safeForm[field.model];
            return !!(v && (v.presigned_url || v.preview_url || v.original_url || v.url));
        },

        open_file_preview(field_index, field) {
            this.selected_url = this.file_url(field_index, field);
            this.preview_modal = true;
        },

        file_url(field_index, field) {
            const safeForm = this.get_safe_form(field_index);
            if (!safeForm) return "";

            const v = safeForm[field.model] || {};
            return typeof v === "string"
                ? v
                : (v.presigned_url || v.preview_url || v.original_url || v.url || "");
        },

        close_the_file_preview() {
            this.preview_modal = false;
            this.selected_url = null;
        },

        file_name(field_index, field) {
            const safeForm = this.get_safe_form(field_index);
            if (!safeForm) return "";

            const v = safeForm[field.model];
            const named = v?.original_name || v?.name || v?.file_name;
            if (named) return named;

            const url = this.file_url(field_index, field);
            if (!url) return "";

            try {
                const u = new URL(url);
                return decodeURIComponent((u.pathname.split("/").pop() || "").split("?")[0]);
            } catch {
                const last = (url.split("?")[0].split("/").pop() || "");
                try { return decodeURIComponent(last); } catch { return last; }
            }
        },

        handle_file_upload(e, field, field_index = this.currentFormIndex) {
            if (this.isReadOnly) {
                if (e?.target) {
                    e.target.value = "";
                }
                this.show_info("This advert is closed. File updates are disabled.");
                return;
            }

            const file = e?.target?.files?.[0];
            if (!file) return;

            // All file uploads must be PDF
            if (file.type !== 'application/pdf') {
                this.show_error("Only PDF files are allowed.");
                if (e?.target) e.target.value = "";
                return;
            }

            const preview = URL.createObjectURL(file);
            const cur = this.get_safe_form(field_index) || {};

            this.setSafeForm(field_index, {
                ...cur,
                [field.model]: {
                    file,
                    file_name: file.name,
                    size: file.size,
                    type: file.type,
                    presigned_url: preview,
                    preview_url: preview,
                    original_url: null
                },
            });
        },

        normalize_dependant_question(dep, normalize_options) {
            const dep_type = this.map_question_type(dep.question_type);
            const dep_latest = dep.latest_answer?.answer ?? null;
            const dep_default = this.coerce_default_by_type(dep_type, dep_latest);

            const data = {
                id: dep.id,
                question_id: dep.question_id,
                section_question_id: dep.id,
                label: dep.question_text || "",
                model: `dq_${dep.question_id}`,
                type: dep_type,
                required: !!dep.is_mandatory,
                trigger_condition: dep.trigger_condition || "",
                options: normalize_options(dep.options),
                __default: dep_default,
                is_visible: false,

                //nested dependents (recursive data)
                dependent_questions: Array.isArray(dep.dependant_questions)
                    ? dep.dependant_questions.map(child =>
                        this.normalize_dependant_question(child, normalize_options)
                    )
                    : [],
            };

            return data;
        },



        remove_file(field_index, model) {
            const cur = this.get_safe_form(field_index) || {};
            const existing = Reflect.get(cur, model);

            if (existing?.preview_url) {
                URL.revokeObjectURL(existing.preview_url);
            }

            const updated = { ...cur, [model]: null };
            this.setSafeForm(field_index, updated);
            const reasonKey = `${model}_file_comment`;
            if (Reflect.has(updated, reasonKey)) {
                Reflect.set(updated, reasonKey, "");
            }
        },

        get_file_url(v) {
            if (!v) return "";
            return typeof v === "string"
                ? v
                : v.presigned_url || v.original_url || v.preview_url || "";
        },

        get_file_name(v) {
            if (!v) return "";
            const named = v?.original_name || v?.name || v?.file_name;
            if (named) return named;

            const url = this.get_file_url(v);
            if (!url) return "";

            try {
                const { pathname } = new URL(url);
                const last = pathname.split("/").pop() || "";
                return decodeURIComponent(last);
            } catch (e) {
                const noQuery = url.split("?")[0];
                const last = noQuery.split("/").pop() || "";
                try { return decodeURIComponent(last); } catch { return last; }
            }
        },

        load_tabs_details() {
            this.isLoading = true;

            const _safeInt = (v) => { const n = Number(v); return v != null && !isNaN(n) ? n : null; };
            const request_params = {
                form_main_section_id: _safeInt(this.form_main_section_id),
                application_form_id: _safeInt(this.application_form_id),
                application_id: _safeInt(this.application_id),
                entity_profile_id: _safeInt(this.entity_profile_id),
                current_status_id: _safeInt(this.current_status_id),
            };

            return axios
                .get(GET_TABS_DETAILS, { headers: get_headers(), params: request_params })
                .then(({ data }) => {
                    if (data?.status !== "success" || !Array.isArray(data?.data)) {
                        this.safeFormConfig = [];
                        this.safeForms = [];
                        this.open_map = {};
                        return;
                    }

                    this._raw_api_data = data.data;
                    const form_config = this.build_form_config(data.data);
                    const safe_forms = this.init_safe_forms_from_config(form_config);

                    this.safeFormConfig = form_config;
                    this.safeForms = safe_forms;
                    this.allowed_actions = data.allowed_actions || [];
                    this.init_open_map();
                    this.restore_dependent_question_visibility();
                    return this.$nextTick();
                })
                .catch((e) => {
                    this.show_error?.(e?.response?.data?.message || e.message || "Failed to load tab details");
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        _parse_we_blocks(questions) {
            // Group questions by [WE N] prefix and build blocks from actual answers
            const blocks_map = {};
            (questions || []).forEach(q => {
                const label = q.question_text || '';
                const match = label.match(/^\[WE\s+(\d+)\]\s*(.*)/i);
                if (!match) return;
                const block_num = parseInt(match[1], 10);
                const field_label = match[2];
                const answer = q.latest_answer?.answer ?? '';
                const lower = field_label.toLowerCase();
                let type = 'text';
                if (lower.includes('month')) type = 'we_month';
                else if (lower.includes('year')) type = 'we_year';
                else if (lower.includes('reason')) type = 'textarea';

                if (!blocks_map[block_num]) blocks_map[block_num] = [];
                blocks_map[block_num].push({
                    label: field_label,
                    value: answer || '',
                    section_question_id: q.id,
                    model: `q_${q.id}`,
                    type,
                });
            });

            const block_nums = Object.keys(blocks_map).map(Number).sort();
            // Only return blocks that have at least one non-empty answer
            return block_nums
                .map((num, idx) => ({
                    index: idx,
                    currently_work_here: false,
                    fields: blocks_map[num],
                }))
                .filter(block => block.fields.some(f => f.value !== ''));
        },

        build_form_config(api_forms) {
            return api_forms.map((main) => {
                const sections = (main.sections || []).map((sec) => {
                    const is_we = !!sec.is_work_experience;
                    const fields = is_we
                        ? []
                        : (sec.questions || []).map((q) => this.normalize_question_to_field(q, sec.section_id));
                    const we_blocks = is_we ? this._parse_we_blocks(sec.questions) : [];

                    return {
                        id: sec.section_id ?? sec.id,
                        title: sec.section_name || "",
                        is_verified: this.normalize_is_verified(sec.is_verified),
                        is_work_experience: is_we,
                        we_blocks,
                        fields,
                        access: sec.access || {}
                    };
                });

                return {
                    _mainId: main.main_section_id ?? main.id,
                    formTitle: main.name || "",
                    sections,
                };
            });
        },

        normalize_question_to_field(q, section_id) {
            const model = `q_${q.question_id}`;

            const type_map = {
                short_text: QUESTION_TYPES.TEXT,
                long_text: QUESTION_TYPES.TEXTAREA,
                number: QUESTION_TYPES.NUMBER,
                date: QUESTION_TYPES.DATE,
                single_select: QUESTION_TYPES.SINGLE_SELECT,
                multi_select: QUESTION_TYPES.MULTI_SELECT,
                file: QUESTION_TYPES.FILE,
                facial_verification: QUESTION_TYPES.FACIAL_VERIFICATION,
                sa_phone_number: QUESTION_TYPES.SA_PHONE_NUMBER,
                telephone: QUESTION_TYPES.TELEPHONE,
                fax: QUESTION_TYPES.FAX,
                email: QUESTION_TYPES.EMAIL,
                postal_code: QUESTION_TYPES.POSTAL_CODE,
                passport_number: QUESTION_TYPES.PASSPORT_NUMBER,
                pdf_file: QUESTION_TYPES.PDF_FILE
            };
            const type = type_map[q.question_type] || QUESTION_TYPES.TEXT;

            const pick_first = (...candidates) =>
                candidates.find(candidate => candidate !== undefined && candidate !== null);

            const get_option_text = (option, index) =>
                String(
                    pick_first(
                        option.choice,
                        option.text,
                        option.label,
                        option.value,
                        `Option ${index + 1}`
                    )
                );

            const get_option_value = (option, text_value) =>
                String(
                    pick_first(
                        option.choice,
                        option.value,
                        option.id,
                        text_value
                    )
                );

            const normalize_options = (options) => {
                if (!Array.isArray(options)) return [];
                return options.map((option, index) => {
                    const text = get_option_text(option, index);
                    const value = get_option_value(option, text);
                    return { value, text };
                });
            };

            const latest = q.latest_answer?.answer ?? null;
            const default_value = this.coerce_default_by_type(type, latest);

            const dependent_questions = Array.isArray(q.dependant_questions)
                ? q.dependant_questions.map((dependent) =>
                    this.normalize_dependant_question(dependent, normalize_options)
                )
                : [];


            const normalized_question = {
                id: q.id,
                question_id: q.question_id,
                section_id,
                model,
                label: q.question_text || "",
                type,
                required: !!q.is_mandatory,
                options: normalize_options(q.options),
                __default: default_value,
                dependent_questions
            };
            return normalized_question;
        },

        map_question_type(type) {
            const type_map = new Map([
                ['short_text', QUESTION_TYPES.TEXT],
                ['long_text', QUESTION_TYPES.TEXTAREA],
                ['single_select', QUESTION_TYPES.SINGLE_SELECT],
                ['radio', QUESTION_TYPES.SINGLE_SELECT],
                ['multi_select', QUESTION_TYPES.MULTI_SELECT],
                ['checkboxes', QUESTION_TYPES.MULTI_SELECT],
                ['file', QUESTION_TYPES.FILE],
                ['number', QUESTION_TYPES.NUMBER],
                ['date', QUESTION_TYPES.DATE],
                ['facial_verification', QUESTION_TYPES.FACIAL_VERIFICATION],
                ['sa_phone_number', QUESTION_TYPES.PHONE],
                ['telephone', QUESTION_TYPES.PHONE],
                ['phone', QUESTION_TYPES.PHONE],
                ['fax', QUESTION_TYPES.FAX],
                ['email', QUESTION_TYPES.EMAIL],
                ['postal_code', QUESTION_TYPES.POSTAL_CODE],
                ['passport_number', QUESTION_TYPES.PASSPORT_NUMBER],
                ['pdf_file', QUESTION_TYPES.PDF_FILE]
            ]);

            return type_map.get(type) || QUESTION_TYPES.TEXT;
        },

        coerce_default_by_type(type, answer) {
            switch (type) {
                case QUESTION_TYPES.TEXT:
                case QUESTION_TYPES.TEXTAREA:
                case QUESTION_TYPES.DATE:
                    return typeof answer === "string" ? answer : "";
                case QUESTION_TYPES.NUMBER: {
                    return answer;
                }
                case QUESTION_TYPES.SINGLE_SELECT: {
                    if (Array.isArray(answer)) return answer.length ? String(answer[0]) : "";
                    return answer ? String(answer) : "";
                }
                case QUESTION_TYPES.MULTI_SELECT:
                    return Array.isArray(answer) ? answer.map((v) => String(v)) : [];
                case QUESTION_TYPES.FILE:
                case QUESTION_TYPES.PDF_FILE:
                case QUESTION_TYPES.FACIAL_VERIFICATION: {
                    let returned_answer = null;
                    if (typeof answer === "string" && answer) {
                        returned_answer = { original_url: answer, presigned_url: "", preview_url: "" };
                    }
                    if (answer && typeof answer === "object") {
                        returned_answer = {
                            original_url: answer.original_url || answer.url || answer.preview_url || answer.presigned_url || "",
                            presigned_url: answer.presigned_url || answer.preview_url || answer.url || "",
                            preview_url: answer.preview_url || answer.presigned_url || answer.url || "",
                            file_name: answer.file_name || answer.name || answer.original_name || ""
                        };
                    }
                    return returned_answer;
                }
                default:
                    return typeof answer === "string" ? answer : "";
            }
        },
        _init_dependents_defaults(store, dependents) {
            if (!Array.isArray(dependents)) return;

            dependents.forEach((dependent_question) => {
                store[dependent_question.model] = dependent_question.__default;
                if (dependent_question.dependent_questions?.length) {
                    this._init_dependents_defaults(store, dependent_question.dependent_questions);
                }
            });
        },

        init_safe_forms_from_config(config) {
            return config.map((form) => {
                const store = {};
                (form.sections || []).forEach((sec) => {
                    // Regular fields
                    (sec.fields || []).forEach((field) => {
                        // Main question
                        store[field.model] = field.__default;

                        // Initialize dependent questions with their default values
                        if (field.dependent_questions && field.dependent_questions.length) {
                            this._init_dependents_defaults(store, field.dependent_questions);
                        }
                    });

                    // WE block fields
                    if (sec.is_work_experience && sec.we_blocks) {
                        sec.we_blocks.forEach(block => {
                            (block.fields || []).forEach(f => {
                                store[f.model] = f.value || '';
                            });
                        });
                    }
                });
                return store;
            });
        },

        flatten_dependents(nodes, parentPath = []) {
            const out = [];
            if (!Array.isArray(nodes)) return out;

            nodes.forEach((node, idx) => {
                const path = [...parentPath, idx + 1];
                out.push({
                node,
                depth: parentPath.length + 1,
                path,
                key: `${node.model}-${path.join("-")}`,
                });

                if (Array.isArray(node.dependent_questions) && node.dependent_questions.length) {
                out.push(...this.flatten_dependents(node.dependent_questions, path));
                }
            });

            return out;
        },

        dependent_indent_class(depth) {
            return depth >= 3 ? "ms-4" : depth === 2 ? "ms-3" : "ms-0";
        },


        restore_dependent_question_visibility() {
            this.safeFormConfig.forEach((form, field_index) => {
                (form.sections || []).forEach((section) => {
                    (section.fields || []).forEach((field) => {
                        if (field.dependent_questions && field.dependent_questions.length) {
                            const safeForm = this.get_safe_form(field_index);
                            const current_value = safeForm ? safeForm[field.model] : undefined;
                            this._update_dependants_visibility(field, current_value, field.dependent_questions, safeForm);
                        }
                    });
                });
            });
        },

        _reset_dependant_values(safeForm, dependent_question) {
            if (!safeForm || !dependent_question) return;
            if (Reflect.has(safeForm, dependent_question.model)) {
                Reflect.set(safeForm, dependent_question.model, dependent_question.__default);
            }
            if (dependent_question.dependent_questions?.length) {
                dependent_question.dependent_questions.forEach(child => this._reset_dependant_values(safeForm, child));
            }
        },

        _update_dependants_visibility(parentField, parentValue, dependents, safeForm) {
            if (!Array.isArray(dependents)) return;

            dependents.forEach((dependent_question) => {
                const visible = this.does_value_match_trigger(parentValue, dependent_question.trigger_condition, parentField);
                dependent_question.is_visible = visible;

                if (!visible) {
                    this._reset_dependant_values(safeForm, dependent_question);
                    return;
                }

                // if visible, recurse using THIS dq's current value as the next parentValue
                const dependent_question_value = safeForm ? Reflect.get(safeForm, dependent_question.model) : undefined;
                this._update_dependants_visibility(dependent_question, dependent_question_value, dependent_question.dependent_questions, safeForm);
            });
        },


        init_open_map() {
            this.open_map = {};
            this.safeFormConfig.forEach((form, field_index) => {
                Reflect.set(this.open_map, field_index, {});
                (form.sections || []).forEach((_, sIdx) => {
                    Reflect.set(Reflect.get(this.open_map, field_index), sIdx, true);
                });
            });
        },

        is_section_open(fIdx, sIdx) {
            if (!Number.isInteger(fIdx) || !Number.isInteger(sIdx) || fIdx < 0 || sIdx < 0) {
                return true;
            }

            if (!Reflect.has(this.open_map, fIdx) ||
                !(typeof Reflect.get(this.open_map, fIdx) === 'object' && Reflect.get(this.open_map, fIdx) !== null) ||
                typeof Reflect.get(Reflect.get(this.open_map, fIdx), sIdx) === 'undefined') {
                return true;
            }

            const form_entry = Reflect.get(this.open_map, fIdx);
            return typeof form_entry === 'object' && form_entry !== null && Reflect.has(form_entry, sIdx) ? Reflect.get(form_entry, sIdx) : true;
        },

        toggle_section(fIdx, sIdx) {
            if (!Number.isInteger(fIdx) || !Number.isInteger(sIdx) || fIdx < 0 || sIdx < 0) {
                return;
            }

            if (!Reflect.has(this.open_map, fIdx)) {
                Reflect.set(this.open_map, fIdx, {});
            }

            const form_entry = Reflect.get(this.open_map, fIdx);
            if (typeof form_entry !== 'object' || form_entry === null) {
                Reflect.set(this.open_map, fIdx, {});
            }

            const validated_form_entry = Reflect.get(this.open_map, fIdx);
            const current_value = this.is_section_open(fIdx, sIdx);
            Reflect.set(validated_form_entry, sIdx, !current_value);
        },

        is_empty(field, field_index) {
            const safeForm = this.get_safe_form(field_index);
            if (!safeForm) return true;

            const val = safeForm[field.model] ?? field.__default;

            switch (field.type) {
                case QUESTION_TYPES.TEXT:
                case QUESTION_TYPES.TEXTAREA:
                case QUESTION_TYPES.DATE:
                    return !val || String(val).trim() === "";
                case QUESTION_TYPES.NUMBER:
                    return val === null || val === undefined || String(val) === "";
                case QUESTION_TYPES.SINGLE_SELECT:
                    return !val || String(val) === "";
                case QUESTION_TYPES.MULTI_SELECT:
                    return !Array.isArray(val) || val.length === 0;
                case QUESTION_TYPES.FILE:
                case QUESTION_TYPES.PDF_FILE:
                    return !val || !(val.presigned_url || val.original_url || val.preview_url);
                case QUESTION_TYPES.FACIAL_VERIFICATION:
                    return !val || !(val.presigned_url || val.original_url || val.preview_url);
                default:
                    return !val || String(val).trim() === "";
            }
        },

        next_form() {
            if (this.currentFormIndex < this.safeFormConfig.length - 1) {
                this.currentFormIndex += 1;
                this.$nextTick(() => {
                    this.restore_dependent_question_visibility_for_form(this.currentFormIndex);
                });
            }
        },

        prev_form() {
            if (this.currentFormIndex > 0) {
                this.currentFormIndex -= 1;
                this.$nextTick(() => {
                    this.restore_dependent_question_visibility_for_form(this.currentFormIndex);
                });
            }
        },

        restore_dependent_question_visibility_for_form(field_index) {
            const form = this.get_safe_form_config(field_index);
            if (!form || !form.sections) return;

            form.sections.forEach((section) => {
                (section.fields || []).forEach((field) => {
                    if (field.dependent_questions && field.dependent_questions.length) {
                        const safeForm = this.get_safe_form(field_index);
                        const current_value = safeForm ? safeForm[field.model] : undefined;
                        this._update_dependants_visibility(field, current_value, field.dependent_questions, safeForm);
                    }
                });
            });
        },

        populate_answered_file_questions(field_index, sIdx) {
            const form = this.get_safe_form_config(field_index);
            if (!form) return;

            const sections = Reflect.get(form, "sections");
            const section = sections ? Reflect.get(sections, sIdx) : undefined;
            const safeForm = this.get_safe_form(field_index);
            if (!section || !safeForm) return;

            this.answered_file_questions = (section.fields || [])
                .filter((f) => f.type === QUESTION_TYPES.FILE || f.type === QUESTION_TYPES.FACIAL_VERIFICATION || f.type === QUESTION_TYPES.PDF_FILE)
                .filter((f) => {
                    const v = safeForm[f.model];
                    return v && (v.presigned_url || v.original_url || v.preview_url);
                })
                .map((f) => ({
                    section_question_id: f.id,
                    question_id: f.question_id,
                    section_id: f.section_id,
                    question_text: f.label,
                }));
        },

        toggle_requested_field(event, field) {
            if (event.target.checked) {
                this.selected_request_fields.push({
                    form_section_id: field.section_id,
                    question_id: field.question_id,
                    requested_field_id: field.section_question_id,
                    requested_field_text: field.question_text
                });
                return;
            }

            this.selected_request_fields = this.selected_request_fields.filter(
                selected => selected.requested_field_id !== field.section_question_id
            );
        },

        section_verification(section_id) {
            const application_form_id = this.application_form_id

            const data = {
                section_id: section_id,
                is_verified: true,
                application_form_id: parseInt(application_form_id, 10),
                verification_comment: "",
            };

            axios.post(VERIFY_SECTION, data, {
                headers: this.headers
            })
                .then(response => {
                    const response_data = response.data;

                    if (response_data.status === 'success') {
                        this.load_tabs_details();
                        this.show_success("Section verified successfully.")
                    } else {
                        this.show_error('Error during the form submit');
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.show_error(error.response.data.message);
                    }
                });
        },

        admin_verify(status) {
            const data = {
                application_form_id: parseInt(this.application_form_id, 10),
                status: status
            };

            axios.post(ADMIN_VERIFY_APPLICATION_URL, data, {
                headers: this.headers
            })
                .then(response => {
                    const response_data = response.data;

                    if (response_data.status === 'success') {
                        this.load_tabs_details();
                        this.show_success("Admin verified successfully.")
                    } else {
                        this.show_error('Error during the admin verification');
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.show_error(error.response.data.message);
                    }
                });
        },

        collect_visible_dependents(parentNode, currentFormData, formatted_answers, file_uploads) {
            if (!parentNode?.dependent_questions?.length) return;

            parentNode.dependent_questions.forEach((dependent_question) => {
                if (dependent_question.is_visible) {
                this.process_question_answer(
                    dependent_question,
                    currentFormData[dependent_question.model],
                    formatted_answers,
                    file_uploads,
                    true
                );

                //recurse into deeper levels
                this.collect_visible_dependents(dependent_question, currentFormData, formatted_answers, file_uploads);
                }
            });
        },


        submitAnswers() {
            if (this.isReadOnly) {
                this.show_info("This advert is closed. This application is read-only.");
                return;
            }

            const formData = new FormData();
            const formatted_answers = [];
            const file_uploads = [];

            const currentForm = this.get_safe_form_config(this.currentFormIndex);
            const currentFormData = this.get_safe_form(this.currentFormIndex);
            const application_form_id = parseInt(this.application_form_id, 10);

            if (!currentForm || !currentFormData) {
                this.show_error("Form data not available");
                return;
            }
            const errors = this.validate_required_fields(currentForm);
            if (Object.keys(errors).length > 0) {
                this.show_info("Please fill in all required fields");
                return;
            }

            // Collect answers for ALL questions (including dependent ones)
            currentForm.sections.forEach(section => {
                // Regular fields
                section.fields.forEach(field => {
                    // Process main question
                    this.process_question_answer(
                        field,
                        currentFormData[field.model],
                        formatted_answers,
                        file_uploads
                    );

                    // Process dependent questions if they exist and are visible
                    this.collect_visible_dependents(field, currentFormData, formatted_answers, file_uploads);
                });

                // WE block fields
                if (section.is_work_experience && section.we_blocks) {
                    section.we_blocks.forEach(block => {
                        (block.fields || []).forEach(f => {
                            const val = currentFormData[f.model];
                            formatted_answers.push({
                                section_question_id: f.section_question_id,
                                answer_text: (val || '').toString().trim(),
                            });
                        });
                    });
                }
            });

            // Track if we have file uploads for emitting event later
            const hasFileUploads = file_uploads.length > 0;

            // Add files to FormData with proper keys
            file_uploads.forEach(file_upload => {
                formData.append(`file_${file_upload.section_question_id}`, file_upload.file);
            });

            // Add other data to FormData
            formData.append('answers', JSON.stringify(formatted_answers));
            formData.append('application_form_id', application_form_id);

            // Submit data using promise chain
            axios.post(SUBMIT_APPLICATION_FORMS_ANSWERS, formData, {
                headers: {
                    ...this.headers,
                    'Content-Type': 'multipart/form-data'
                },
            })
                .then(response => {
                    if (response.data?.status === 'success') {
                        this.show_success('Answers submitted successfully');

                        // Emit event if files were uploaded to trigger document management reload
                        if (hasFileUploads) {
                            this.$emit('files-saved');
                        }

                        // Reload to get updated data
                        this.load_tabs_details();
                    } else {
                        this.show_error(response.data?.message || 'Submission failed');
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.show_error(error.response.data.message);
                    } else {
                        this.show_error(error.message || 'An unexpected error occurred');
                    }
                });
        },

        process_question_answer(question, answerValue, formatted_answers, file_uploads, isDependent = false) {
            const section_question_id = isDependent
                ? question.section_question_id // Use the section_question_id from dependent question
                : question.id; // Use the id from main question

            if (!section_question_id) {
                return;
            }

            // Handle file uploads
            if (question.type === QUESTION_TYPES.FILE || question.type === QUESTION_TYPES.FACIAL_VERIFICATION || question.type === QUESTION_TYPES.PDF_FILE) {
                if (answerValue?.file) {
                    // New file uploaded
                    file_uploads.push({
                        file: answerValue.file,
                        section_question_id: section_question_id
                    });
                    formatted_answers.push({
                        section_question_id: section_question_id,
                        answer_text: answerValue.file.name
                    });
                } else if (answerValue?.original_url) {
                    // Existing file (already uploaded)
                    formatted_answers.push({
                        section_question_id: section_question_id,
                        answer_text: answerValue.original_url
                    });
                } else {
                    // File removed or not provided
                    formatted_answers.push({
                        section_question_id: section_question_id,
                        answer_text: ''
                    });
                }
            }
            // Handle multi-select
            else if (question.type === QUESTION_TYPES.MULTI_SELECT) {
                const answerText = Array.isArray(answerValue)
                    ? answerValue.join(',')
                    : '';
                formatted_answers.push({
                    section_question_id: section_question_id,
                    answer_text: answerText
                });
            }
            // Handle other field types
            else {
                let answerText = answerValue ? answerValue.toString().trim() : '';
                formatted_answers.push({
                    section_question_id: section_question_id,
                    answer_text: answerText
                });
            }
        },

        reset_request_information_state() {
            this.request_info_note = "";
            this.selected_request_fields = [];
            this.answered_file_questions = [];
            this.requested_field_ids = [];
        },

        prepare_request_information(fIdx, sIdx) {
            this.reset_request_information_state();
            this.populate_answered_file_questions(fIdx, sIdx);
        },

        async request_information_submit() {
            if (!this.validate_request_information()) return;

            const application_form_id = this.application_form_id
            var message = this.request_info_note
            var requested_fields = this.selected_request_fields

            const data = {
                application_form_id: application_form_id,
                message: message,
                requested_fields: requested_fields,
            };

            axios.post(REQUEST_INFO_URL, data, {
                headers: this.headers
            })
                .then(response => {
                    const response_data = response.data;

                    if (response_data.status === 'success') {
                        this.close_modal('request_information_modal')
                        this.show_success("Information successfully requested")
                        this.request_info_note = "";
                        this.requested_field_ids = [];
                        this.selected_request_fields = [];
                        this.answered_file_questions = [];
                    } else {
                        this.show_error('Error during the form submit');
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data) {
                        this.show_error(error.response.data.message);
                    }
                });
        },

        validate_request_information() {
            if (!validate_non_empty_string(this.request_info_note)) {
                this.show_error('Request information note must not be null');
                return false;
            }
            if (!validate_non_sub_zero_number(parseInt(this.application_form_id, 10))) {
                this.show_error('Application form id must not be null');
                return false;
            }
            return true;
        },

        update_dependent_questions_visibility(field, current_value) {
            if (!Array.isArray(field.dependent_questions) || !field.dependent_questions.length) return;

            const safeForm = this.get_safe_form(this.currentFormIndex);

            const reset_tree = (node) => {
                if (safeForm && Reflect.has(safeForm, node.model)) {
                    Reflect.set(safeForm, node.model, node.__default);
                }
                node.is_visible = false;

                if (Array.isArray(node.dependent_questions) && node.dependent_questions.length) {
                    node.dependent_questions.forEach((child) => reset_tree(child));
                }
            };

            const update_tree = (parent_field, parent_value, nodes) => {
                if (!Array.isArray(nodes) || !nodes.length) return;

                nodes.forEach((dependent_question) => {
                    const should_be_visible = this.does_value_match_trigger(
                        parent_value,
                        dependent_question.trigger_condition,
                        parent_field
                    );

                    dependent_question.is_visible = should_be_visible;

                    if (!should_be_visible) {
                        reset_tree(dependent_question);
                        return;
                    }

                    // If visible, update nested dependents using dq's own current value
                    const dependent_question_current_value = safeForm ? Reflect.get(safeForm, dependent_question.model) : undefined;

                    if (Array.isArray(dependent_question.dependent_questions) && dependent_question.dependent_questions.length) {
                        update_tree(dependent_question, dependent_question_current_value, dependent_question.dependent_questions);
                    }
                });
            };

            update_tree(field, current_value, field.dependent_questions);
        },


        does_value_match_trigger(value, triggerCondition, field) {
            if (!triggerCondition) return false;
            if (Array.isArray(value)) {
                const selectedValues = value.map(val => String(val).trim().toLowerCase());
                const trigger = String(triggerCondition).trim().toLowerCase();

                return selectedValues.includes(trigger) ||
                    selectedValues.some(val => val === trigger);
            }

            // Handle single values
            let valueToCompare = value;

            // If it's a select field, try to find the corresponding option text
            if (field.options && field.options.length && value !== null && value !== undefined) {
                const option = field.options.find(opt =>
                    String(opt.value).trim().toLowerCase() === String(value).trim().toLowerCase()
                );
                if (option) {
                    valueToCompare = option.text;
                }
            }

            // Compare normalized values
            const normalizedValue = String(valueToCompare || '').trim().toLowerCase();
            const normalizedTrigger = String(triggerCondition || '').trim().toLowerCase();

            return normalizedValue === normalizedTrigger;
        },

        normalize_value(value) {
            if (value === null || value === undefined) return '';
            return String(value).trim().toLowerCase();
        },

        validate_required_fields(form) {
            const errors = {};
            const formData = this.get_safe_form(this.currentFormIndex);

            if (!form || !Array.isArray(form.sections)) {
                return errors;
            }

            form.sections.forEach(section => {
                (section.fields || []).forEach(field => {
                    const value = Reflect.get(formData, field.model);

                    if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
                        Reflect.set(errors, field.model, `This field is mandatory`);
                    }

                    if (field.dependent_questions && field.dependent_questions.length) {
                        field.dependent_questions.forEach(dq => {
                            const dqValue = Reflect.get(formData, dq.model);

                            if (dq.is_visible && dq.required && (!dqValue || (Array.isArray(dqValue) && dqValue.length === 0))) {
                                Reflect.set(errors, dq.model, `Field required "${dq.label || 'this field'}"`);
                            }
                        });
                    }
                });
            });

            return errors;
        }
    },
};
